import React, { useEffect, useMemo, useState } from "react";
import SbDrawer from "uiCore/SbDrawer/SbDrawer";
import SbButton from "uiCore/SbButton/SbButton";
import SbInput from "uiCore/SbInput/SbInput";
import SbSelect from "uiCore/SbSelect/SbSelect";
import SbRow from "uiCore/SbRow/SbRow";
import SbCol from "uiCore/SbCol/SbCol";
import { SbForm, SbInfoTooltip } from "uiCore";
import { fetchOrgStructure } from "Api/apiService";
import useAuthStore from "Store/useAuthStore";
import { DrawerFormStyled, DrawerFooterStyled } from "./SbAdminJobCodeDrawer.styles";

const ROLE_STATUS_OPTIONS = [
  { value: "Active",   label: "Active" },
  { value: "Inactive", label: "Inactive" },
  { value: "Archived", label: "Archived" },
];

// PRD §5.18 — tooltip content strings for Job Code Drawer form fields
const TOOLTIP_JOB_CODE = "The unique identifier for this role type (e.g. SWE-004). Must be unique across your organisation.";
const TOOLTIP_COMPANY_JOB_TITLE = "The internal name for this role as used in your organisation.";
const TOOLTIP_GENERIC_JOB_TITLE = "The generic or standard name for this role (e.g. 'Senior Software Engineer'). Used for industry benchmarking.";
const TOOLTIP_BUSINESS_UNIT = "The business unit this job code belongs to.";
const TOOLTIP_LEGAL_ENTITY = "The legal entity this job code is registered under.";
const TOOLTIP_JOB_LEVEL = "The seniority level for this job code.";
const TOOLTIP_ROLE_TYPE = "The role category (e.g. Technical, Managerial, Support).";
const TOOLTIP_ROLE_STATUS = "Whether this job code is Active, Inactive, or Archived.";

const SbAdminJobCodeDrawer = ({ isOpen, onClose, onSave, saving = false }) => {
  const orgMetaData = useAuthStore((s) => s.orgMetaData);
  const [form] = SbForm.useForm();
  const [orgStructure, setOrgStructure] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const legalEntity = SbForm.useWatch("legalEntity", form);

  useEffect(() => {
    if (!isOpen) return;
    form.resetFields();
    setIsSuccess(false);
    let cancelled = false;
    fetchOrgStructure()
      .then((resp) => { if (!cancelled) setOrgStructure(resp); })
      .catch(() => { if (!cancelled) setOrgStructure(null); });
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (legalEntity) form.setFieldValue("businessUnit", undefined);
  }, [legalEntity]);

  const legalEntityOptions = useMemo(() => {
    const arr = orgStructure?.organization_structure;
    if (!Array.isArray(arr)) return [];
    return arr.map((item) => item?.legal_entity).filter(Boolean).map((v) => ({ value: v, label: v }));
  }, [orgStructure]);

  const businessUnitOptions = useMemo(() => {
    const arr = orgStructure?.organization_structure;
    if (!Array.isArray(arr)) return [];
    if (legalEntity) {
      const selected = arr.find((x) => x?.legal_entity === legalEntity);
      const bus = selected?.business_units;
      if (!Array.isArray(bus)) return [];
      return bus.map((bu) => bu?.business_unit).filter(Boolean).map((v) => ({ value: v, label: v }));
    }
    const all = [];
    arr.forEach((entity) => {
      (entity?.business_units ?? []).forEach((bu) => {
        const val = bu?.business_unit;
        if (val && !all.includes(val)) all.push(val);
      });
    });
    return all.map((v) => ({ value: v, label: v }));
  }, [orgStructure, legalEntity]);

  const roleTypeOptions = useMemo(() => {
    const types = orgMetaData?.company_structure?.find((item) => item?.role_type)?.role_type ?? [];
    return Array.isArray(types) ? types.filter(Boolean).map((t) => ({ value: t, label: t })) : [];
  }, [orgMetaData]);

  const jobLevelOptions = useMemo(() => {
    const gradeData = orgMetaData?.company_structure?.find((item) => item?.job_grade)?.job_grade ?? [];
    if (!Array.isArray(gradeData) || !gradeData.length) return [];
    if (typeof gradeData[0] === "object") {
      return gradeData
        .map((g) => ({ value: `${g?.level} - ${g?.grade_title}`.trim(), label: `${g?.level} - ${g?.grade_title}`.trim() }))
        .filter((x) => x.value && x.value !== "-");
    }
    return gradeData.filter(Boolean).map((v) => ({ value: v, label: v }));
  }, [orgMetaData]);

  const handleFinish = async (values) => {
    try {
      const apiPayload = {
        job_code:         values.jobCode?.trim(),
        job_title:        values.companyJobTitle?.trim(),
        job_family_name:  values.genericJobTitle?.trim(),
        business_unit:    values.businessUnit,
        legal_entity:     values.legalEntity,
        level:            values.jobLevel,
        role_type:        values.roleType,
        role_status:      values.roleStatus,
        work_environment: "Hybrid",
      };
      const ok = await onSave?.(apiPayload);
      if (ok) {
        setIsSuccess(true);
      }
    } catch {
      // error toast is shown via Redux with the backend message
    }
  };

  const formFooter = (
    <DrawerFooterStyled>
      <SbButton buttonType="secondary" onClick={onClose} disabled={saving}>
        Cancel
      </SbButton>
      <SbButton buttonType="primary" onClick={() => form.submit()} loading={saving}>
        Save
      </SbButton>
    </DrawerFooterStyled>
  );

  if (isSuccess) {
    return (
      <SbDrawer
        open={isOpen}
        onClose={onClose}
        title="Create New Role (Job Code)"
        width="45vw"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={{ fontWeight: 600 }}>Job code added successfully.</div>
          <div style={{ color: "#6B7280" }}>
            Please refresh once to see the new job code in Job Library / Create JD dropdown.
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "4px" }}>
            <SbButton buttonType="secondary" onClick={onClose}>Close</SbButton>
            <SbButton buttonType="primary" onClick={() => window.location.reload()}>Refresh now</SbButton>
          </div>
        </div>
      </SbDrawer>
    );
  }

  return (
    <SbDrawer
      open={isOpen}
      onClose={onClose}
      title="Create New Role (Job Code)"
      width="45vw"
      footer={formFooter}
    >
      <DrawerFormStyled form={form} layout="vertical" onFinish={handleFinish}>
        <SbForm.Item
          name="jobCode"
          label={<SbInfoTooltip content={TOOLTIP_JOB_CODE}>Job Code</SbInfoTooltip>}
          rules={[{ required: true, message: "Job Code is required" }]}
        >
          <SbInput placeholder="Enter new job code" />
        </SbForm.Item>

        <SbForm.Item
          name="companyJobTitle"
          label={<SbInfoTooltip content={TOOLTIP_COMPANY_JOB_TITLE}>Company Specific Job / Role Title</SbInfoTooltip>}
          rules={[{ required: true, message: "Company job title is required" }]}
        >
          <SbInput placeholder="What would you call this role in your company?" />
        </SbForm.Item>

        <SbForm.Item
          name="genericJobTitle"
          label={<SbInfoTooltip content={TOOLTIP_GENERIC_JOB_TITLE}>Generic or Industry Job / Role Title</SbInfoTooltip>}
          rules={[{ required: true, message: "Generic job title is required" }]}
        >
          <SbInput placeholder="What is the commonly accepted title for this role in your industry?" />
        </SbForm.Item>

        <SbRow gutter={16}>
          <SbCol span={12}>
            <SbForm.Item
              name="legalEntity"
              label={<SbInfoTooltip content={TOOLTIP_LEGAL_ENTITY}>Legal Entity</SbInfoTooltip>}
              rules={[{ required: true, message: "Legal Entity is required" }]}
            >
              <SbSelect options={legalEntityOptions} placeholder="Select legal entity" />
            </SbForm.Item>
          </SbCol>
          <SbCol span={12}>
            <SbForm.Item
              name="businessUnit"
              label={<SbInfoTooltip content={TOOLTIP_BUSINESS_UNIT}>Business Unit</SbInfoTooltip>}
              rules={[{ required: true, message: "Business Unit is required" }]}
            >
              <SbSelect options={businessUnitOptions} placeholder="Select business unit" />
            </SbForm.Item>
          </SbCol>
        </SbRow>

        <SbRow gutter={16}>
          <SbCol span={12}>
            <SbForm.Item
              name="roleType"
              label={<SbInfoTooltip content={TOOLTIP_ROLE_TYPE}>Role Type</SbInfoTooltip>}
              rules={[{ required: true, message: "Role Type is required" }]}
            >
              <SbSelect options={roleTypeOptions} placeholder="Select role type" />
            </SbForm.Item>
          </SbCol>
          <SbCol span={12}>
            <SbForm.Item
              name="jobLevel"
              label={<SbInfoTooltip content={TOOLTIP_JOB_LEVEL}>Job Level</SbInfoTooltip>}
              rules={[{ required: true, message: "Job Level is required" }]}
            >
              <SbSelect options={jobLevelOptions} placeholder="Select job level" />
            </SbForm.Item>
          </SbCol>
        </SbRow>

        <SbForm.Item
          name="roleStatus"
          label={<SbInfoTooltip content={TOOLTIP_ROLE_STATUS}>Role Status</SbInfoTooltip>}
          rules={[{ required: true, message: "Role Status is required" }]}
        >
          <SbSelect options={ROLE_STATUS_OPTIONS} placeholder="Select role status" />
        </SbForm.Item>
      </DrawerFormStyled>
    </SbDrawer>
  );
};

export default SbAdminJobCodeDrawer;
